<script setup lang="ts">
  import type { DateValue } from '@internationalized/date';
  import { getLocalTimeZone, parseDate, today } from '@internationalized/date';
  import dayjs from 'dayjs';
  import { computed, onMounted, ref, watch } from 'vue';

  import type { DunningState, Partner } from '@stuut/api/models';
  import { DunningStateAction } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { Calendar } from '@ui/components/calendar';
  import { Popover, PopoverContent, PopoverTrigger } from '@ui/components/popover';
  import { Skeleton } from '@ui/components/skeleton';
  import { toast } from '@ui/components/sonner';
  import { Timestamp } from '@ui/components/time';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';

  import { stuut } from '@/api';

  interface Props {
    partner: Partner;
  }
  const props = defineProps<Props>();

  const open = ref(false);
  const isLoading = ref(false);
  const isSaving = ref(false);
  const isXlsToggling = ref(false);
  const dunningStates = ref<DunningState[]>([]);

  // A pause state the user clicked but hasn't committed yet — its expiry is being chosen.
  const pendingPauseStateId = ref<string | null>(null);
  const pauseUntil = ref<DateValue>();
  const dateOpen = ref(false);

  // Expiry is optional (no date = indefinite pause); when set it must be in the future.
  const minPauseDate = computed(() => today(getLocalTimeZone()).add({ days: 1 }));

  // The expiry is inclusive and evaluated in the customer's local time (server-side).
  const pauseTooltip =
    "Paused until the end of this date in the customer's local time, leave empty to pause indefinitely.";

  const sortedDunningStates = computed(() => {
    return [...dunningStates.value].sort((a, b) => {
      if (a.name === 'Dunning') return -1;
      if (b.name === 'Dunning') return 1;

      if (a.name === 'Other') return 1;
      if (b.name === 'Other') return -1;

      return a.name.localeCompare(b.name);
    });
  });

  const currentStateId = computed(() => props.partner.dunning_state_id);

  const loadDunningStates = async () => {
    if (isLoading.value) return;
    isLoading.value = true;
    try {
      const resp = await stuut.dunningStates.list({}, true);
      dunningStates.value = resp.data;
    } catch (e: any) {
      toast.error(e?.clientMessage || 'Failed to load dunning states');
    } finally {
      isLoading.value = false;
    }
  };

  watch(open, (val) => {
    if (val && dunningStates.value.length === 0) loadDunningStates();
    if (val) {
      pendingPauseStateId.value = null;
      pauseUntil.value = undefined;
    }
  });

  onMounted(() => {
    loadDunningStates();
  });

  const toggleXlsAttachments = async (checked: boolean) => {
    if (isXlsToggling.value) return;
    isXlsToggling.value = true;
    try {
      await stuut.partners.update(
        props.partner.id,
        { data: { is_xls_attachments_enabled: checked } },
        props.partner,
      );
      toast.success(`XLS attachments ${checked ? 'enabled' : 'disabled'}`);
    } catch (error: any) {
      toast.error(error.clientMessage || 'Failed to update XLS attachments setting');
    } finally {
      isXlsToggling.value = false;
    }
  };

  const commitState = async (stateId: string, pauseUntilDate: string | null) => {
    if (isSaving.value) return;
    isSaving.value = true;
    try {
      await stuut.partners.update(
        props.partner.id,
        { data: { dunning_state: stateId, dunning_state_pause_until: pauseUntilDate } },
        props.partner,
      );
      pendingPauseStateId.value = null;
      toast.success('Dunning state updated');
      open.value = false;
    } catch (err: any) {
      toast.error(err?.clientMessage || 'Failed to update dunning state');
    } finally {
      isSaving.value = false;
    }
  };

  const onSelectState = (stateId: string) => {
    if (isSaving.value) return;
    if (!stateId) {
      open.value = false;
      return;
    }
    const state = dunningStates.value.find((s) => s.id === stateId);
    const isPause = state?.action === DunningStateAction.OUTREACH_PAUSE;

    // Re-clicking the current state: only meaningful for a pause (edit its expiry); else just close.
    if (stateId === currentStateId.value) {
      if (!isPause) {
        open.value = false;
        return;
      }
      pendingPauseStateId.value = stateId;
      pauseUntil.value = props.partner.dunning_state_pause_until
        ? parseDate(props.partner.dunning_state_pause_until)
        : undefined;
      return;
    }

    // Pause states can carry an optional expiry — defer commit until the user clicks Apply.
    if (isPause) {
      pendingPauseStateId.value = stateId;
      pauseUntil.value = undefined;
      return;
    }
    // Enable states commit instantly (one click), clearing any expiry.
    commitState(stateId, null);
  };

  const applyPause = () => {
    if (!pendingPauseStateId.value) return;
    commitState(pendingPauseStateId.value, pauseUntil.value?.toString() ?? null);
  };
</script>

<template>
  <Popover v-model:open="open">
    <PopoverTrigger as-child>
      <Button variant="secondary" size="sm" :loading="isSaving">
        <i class="fa" :class="partner?.isOutreachPaused ? 'fa-pause' : 'fa-play text-success'" />
        <span class="whitespace-nowrap">
          {{ partner?.isOutreachPaused ? 'Dunning Paused' : 'Dunning Active' }}
          <template v-if="partner?.dunning_state_pause_until">
            until
            <Timestamp
              :time="dayjs(partner.dunning_state_pause_until)"
              date-only
              format="MMM D YYYY"
            />
          </template>
        </span>

        <i
          class="fa text-2xs! text-muted-foreground"
          :class="open ? 'fa-chevron-up' : 'fa-chevron-down'"
        />
      </Button>
    </PopoverTrigger>

    <PopoverContent class="w-[460px] max-w-[460px] p-0" align="end" side="bottom" :side-offset="6">
      <div class="flex items-center justify-between p-4 border-b">
        <p class="text-sm font-semibold text-foreground">Select Dunning State</p>
        <button
          class="inline-flex items-center gap-1.5 rounded-sm px-2 py-1 text-xs transition-colors"
          :class="
            partner.is_xls_attachments_enabled
              ? 'bg-success/10 text-success'
              : 'bg-muted text-muted-foreground'
          "
          :disabled="isXlsToggling"
          @click.stop="toggleXlsAttachments(!partner.is_xls_attachments_enabled)"
        >
          <i class="far fa-file-excel" />
          <span>XLS</span>
        </button>
      </div>

      <div class="max-h-[360px] overflow-y-auto">
        <div v-if="isLoading" class="flex gap-3 px-4 py-3">
          <Skeleton class="h-16 w-full" />
        </div>

        <div
          v-else-if="dunningStates.length === 0"
          class="px-4 py-3 text-xs text-foreground opacity-90"
        >
          No dunning states found.
        </div>

        <div v-else class="divide-y">
          <div
            v-for="s in sortedDunningStates"
            :key="s.id"
            type="button"
            class="w-full px-4 py-3 text-left"
            :class="pendingPauseStateId === s.id ? 'bg-accent' : 'hover:bg-accent'"
            :disabled="isSaving"
            @click="onSelectState(s.id)"
          >
            <div class="grid grid-cols-[auto_1fr_auto] gap-3 items-center">
              <div class="w-6 flex justify-center">
                <i
                  class="fa-check-circle text-base"
                  :class="[
                    s.id === currentStateId && s.id !== pendingPauseStateId ? 'fa' : 'far',
                    s.id === pendingPauseStateId
                      ? 'text-primary'
                      : s.id === currentStateId
                        ? 'text-success'
                        : 'text-muted-foreground opacity-50',
                  ]"
                />
              </div>

              <div class="flex flex-col">
                <div class="text-sm text-foreground">
                  {{ s.name }}
                  <span
                    v-if="s.id === currentStateId && partner.dunning_state_pause_until"
                    class="text-2xs text-muted-foreground"
                  >
                    · until
                    <Timestamp
                      :time="dayjs(partner.dunning_state_pause_until)"
                      date-only
                      format="MMM D YYYY"
                    />
                  </span>
                </div>
                <div class="text-2xs text-foreground opacity-80">
                  {{ s.description }}
                </div>
              </div>

              <div class="w-6 flex justify-center">
                <i
                  class="fa text-lg"
                  :class="
                    s.action === DunningStateAction.OUTREACH_ENABLE
                      ? 'fa-play text-success'
                      : 'fa-pause text-muted-foreground'
                  "
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="pendingPauseStateId" class="flex items-center justify-between gap-3 p-4 border-t">
        <div class="flex items-center gap-2">
          <span class="text-sm text-foreground whitespace-nowrap shrink-0">Pause until</span>

          <Popover v-model:open="dateOpen">
            <PopoverTrigger as-child>
              <Button variant="outline" size="sm">
                <i class="fa fa-calendar" />
                <Timestamp
                  v-if="pauseUntil"
                  :time="dayjs(pauseUntil.toString())"
                  date-only
                  format="MMM D YYYY"
                />
                <span v-else>Set date</span>
              </Button>
            </PopoverTrigger>

            <PopoverContent class="w-auto p-3" align="start">
              <Calendar
                v-model="pauseUntil"
                :min-value="minPauseDate"
                @update:model-value="dateOpen = false"
              />
            </PopoverContent>
          </Popover>
          <Tooltip>
            <TooltipTrigger as-child>
              <i class="far fa-circle-info text-xs text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent class="max-w-[260px]">{{ pauseTooltip }}</TooltipContent>
          </Tooltip>
          <Button
            v-if="pauseUntil"
            variant="ghost"
            size="icon-sm"
            title="Clear date"
            @click="pauseUntil = undefined"
          >
            <i class="far fa-xmark text-2xs" />
          </Button>
        </div>

        <div class="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            :disabled="isSaving"
            @click="pendingPauseStateId = null"
          >
            Cancel
          </Button>
          <Button size="sm" :loading="isSaving" @click="applyPause">Apply</Button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
